import {
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  List,
  TextField,
  FormLabel,
} from '@material-ui/core';
import { fromJS } from 'immutable';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Field } from 'redux-form/immutable';
import Tooltip from '@mui/material/Tooltip';
import InfoOutlined from '@mui/icons-material/InfoOutlined';

import FormFieldLabel from 'retired/FormFieldLabel';
import FormCheckbox from 'retired/FormCheckbox';
import LocationSelector from 'retired/LocationSelector';
import SelectableChip from 'retired/SelectableChip';
import OrganizationApi from 'entities/organization/api';
import { useDebounce } from 'utils/useDebounce';
import makeSortableItem from 'retired/SortableItem';
import { selectUser } from 'entities/user/selectors';

import Option from './Option';
import SelectedOption from './SelectedOption';

const ConnectedSelectedOption = makeSortableItem({
  itemType: 'content',
  wrapperStyle: {
    position: 'relative',
    maxWidth: 1000,
    paddingTop: 6,
    paddingBottom: 2,
    padding: 0,
    marginBottom: 10,
  },
  overlayStyle: {
    top: -7,
    bottom: -7,
  },
})(({ fieldName, index, fields, isEditable }) => {
  const item = fields.get(index);
  // Memoize the remove function to keep referential equality of SelectedOption
  const remove = useCallback(() => fields.remove(index), []);
  return (
    <SelectedOption
      key={item.get('id')}
      fieldName={fieldName}
      nonprofit={item}
      remove={remove}
      isEditable={isEditable}
    />
  );
});

ConnectedSelectedOption.propTypes = {
  fieldName: PropTypes.string.isRequired,
  index: PropTypes.number.isRequired,
  fields: PropTypes.object.isRequired,
  isEditable: PropTypes.bool.isRequired,
};

const FormNonprofitsSelector = ({
  fields,
  initialLocation = '',
  limit = null,
  nonprofitsAreEditable = true,
  required = false,
  forFundraiser = false,
  acceptsDonations,
  meta,
  showAutoTagDonations = false,
  showLinkToDonateSearch = false,
}) => {
  const [options, setOptions] = useState(fromJS([]));
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedLocation, setSelectedLocation] = useState(initialLocation);
  const [selectMultipleNonprofits, setSelectMultipleNonprofits] = useState(false);
  const [fieldsLoaded, setFieldsLoaded] = useState(false);
  const [multipleNonprofitsSubscriptions, setMultipleNonprofitsSubscriptions] = useState([]);
  const user = useSelector(selectUser);

  const debouncedSearchTerm = useDebounce(searchTerm);
  const debouncedSelectedLocation = useDebounce(selectedLocation);
  const debouncedSelectMultipleNonprofits = useDebounce(selectMultipleNonprofits);

  useEffect(() => {
    if (selectedLocation !== 'US' && forFundraiser && fieldsLoaded) {
      setSelectMultipleNonprofits(false);
    }

    if (multipleNonprofitsSubscriptions.length) {
      multipleNonprofitsSubscriptions.forEach((subscription) => {
        subscription.unsubscribe();
      });
    }

    const updateOptions = () => {
      let searchNonprofitsFetch = null;
      if (searchTerm.length > 2) {
        setLoading(true);

        const searchNonprofitDB = user?.organization?.settings?.get('searchNonprofitDB') || false;

        searchNonprofitsFetch = OrganizationApi.searchNonprofits(
          {
            searchTerm,
            countryCode: selectedLocation,
            acceptsDonations,
            donationProvider: selectMultipleNonprofits && forFundraiser ? 'NFG' : undefined,
          },
          searchNonprofitDB
        ).subscribe((results) => {
          setOptions(fromJS(results));
          setLoading(false);
        });
      } else {
        setOptions(fromJS([]));
      }

      return searchNonprofitsFetch;
    };
    const updateOptionsRes = updateOptions();

    if (updateOptionsRes) {
      setMultipleNonprofitsSubscriptions([...multipleNonprofitsSubscriptions, updateOptionsRes]);
    }
  }, [debouncedSearchTerm, debouncedSelectedLocation, debouncedSelectMultipleNonprofits]);

  useEffect(() => {
    if (fields.getAll() && !fieldsLoaded) {
      setSelectMultipleNonprofits(fields.getAll().size > 1);
      if (fields.getAll().size > 1) {
        setSelectedLocation('US');
      }
      setFieldsLoaded(true);
    }
  }, [fields, fieldsLoaded]);

  useEffect(() => {
    if (!selectMultipleNonprofits && fieldsLoaded) {
      fields.removeAll();
    }
  }, [selectMultipleNonprofits]);

  const selectedNonprofits = fields.getAll() || [];

  return (
    <>
      <Grid container spacing={3}>
        <Grid item md={6}>
          <LocationSelector
            onSelect={(x) => setSelectedLocation(x)}
            selectedLocation={selectedLocation}
          />
          <Grid container justifyContent="space-between">
            <Grid
              item
              style={{
                width: '80%',
                marginTop: 16,
              }}
            >
              <FormControl
                style={{
                  width: '100%',
                  height: 17,
                }}
              >
                <InputLabel shrink>
                  {!selectMultipleNonprofits ? 'Select a nonprofit' : 'Select your nonprofits'}
                  {required ? '*' : ''}
                </InputLabel>
              </FormControl>
            </Grid>
            {selectedLocation === 'US' && forFundraiser && (
              <Grid item>
                <FormCheckbox
                  input={{
                    onChange: () => setSelectMultipleNonprofits(!selectMultipleNonprofits),
                    value: selectMultipleNonprofits,
                  }}
                  name="selectMultipleNonprofits"
                  label="Add multiple nonprofits to this fundraiser"
                ></FormCheckbox>
              </Grid>
            )}
            <Grid item>
              <SelectableChip
                onClick={() => fields.removeAll()}
                label="Unset"
                style={{ margin: 0 }}
              />
            </Grid>
          </Grid>
          <TextField
            placeholder="Search by nonprofit name or EIN"
            disabled={!selectedLocation}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ width: '100%' }}
          />
          <List
            style={{
              maxHeight: 450,
              overflow: 'auto',
            }}
          >
            {loading ? (
              <div
                style={{
                  textAlign: 'center',
                  marginTop: 20,
                  marginBottom: 20,
                }}
              >
                <CircularProgress />
              </div>
            ) : (
              options
                .map((item) => {
                  const selected = selectedNonprofits.find((i) => i.get('id') === item?.get('id'));
                  const add = () => {
                    if (fields.length === limit) {
                      // eslint-disable-next-line no-alert
                      alert(
                        limit > 1
                          ? `You cannot select more than ${limit} nonprofits`
                          : `You can only select a single nonprofit`
                      );
                    } else if (!selectMultipleNonprofits && forFundraiser) {
                      fields.removeAll();
                      fields.push(item);
                    } else {
                      fields.push(item);
                    }
                  };
                  return !selected && <Option key={item.get('id')} add={add} nonprofit={item} />;
                })
                .filter(Boolean)
            )}
          </List>
        </Grid>
        <Grid item md={6}>
          <FormFieldLabel>
            {selectMultipleNonprofits || !forFundraiser
              ? `Selected organizations ${required ? '*' : ''}`
              : `Selected organization ${required ? '*' : ''}`}
          </FormFieldLabel>
          <List
            style={{
              maxHeight: 560,
              overflow: 'auto',
            }}
          >
            {fields.map((fieldName, index) => (
              <ConnectedSelectedOption
                // eslint-disable-next-line react/no-array-index-key
                key={index}
                fieldName={fieldName}
                index={index}
                fields={fields}
                isEditable={nonprofitsAreEditable}
                move={fields.move}
              />
            ))}
          </List>
        </Grid>
      </Grid>
      {meta.error && (meta.dirty || meta.submitFailed) && (
        <FormLabel error style={{ fontSize: 12, marginTop: 5 }}>
          {meta.error}
        </FormLabel>
      )}

      {user?.isAdmin && (
        <>
          {showLinkToDonateSearch && (
            <div style={{ marginTop: 10 }}>
              <Field
                name="showLinkToDonateSearch"
                component={FormCheckbox}
                label="Show Nonprofit Searchbar"
              />
              <Tooltip title="By enabling this, a Nonprofit Search Bar appears at the bottom of the campaign’s nonprofit section, allowing users to initiate a search for nonprofits or fundraisers directly from the campaign.">
                <InfoOutlined />
              </Tooltip>
            </div>
          )}
          {showAutoTagDonations && (
            <div style={{ marginTop: 10 }}>
              <Field name="autoTagDonations" component={FormCheckbox} label="Universal Campaign" />
              <Tooltip
                title={`
                  By making your campaign a "Universal" campaign, you enable all donations across the platform to count towards this campaign during the campaign period, so long as they were not already part of another active campaign. 

                  This means it's not required to add any nonprofits to the campaign directly. We do however recommend that you still add a few as that makes it easier for your employees to choose!

                  Please note that only one universal campaign can be active at one time.
              `}
              >
                <InfoOutlined />
              </Tooltip>
            </div>
          )}
        </>
      )}
    </>
  );
};
FormNonprofitsSelector.propTypes = {
  fields: PropTypes.object,
  meta: PropTypes.object,
  initialLocation: PropTypes.string,
  limit: PropTypes.number,
  nonprofitsAreEditable: PropTypes.bool,
  required: PropTypes.bool,
  forFundraiser: PropTypes.bool,
  acceptsDonations: PropTypes.bool,
  showAutoTagDonations: PropTypes.bool,
  showLinkToDonateSearch: PropTypes.bool,
};
export default FormNonprofitsSelector;
